import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  MessageSquare,
  Search,
  Star,
  X,
  XCircle,
} from "lucide-react";
import { getClearlineStatusChip } from "../utils/statusMapping";
import { formatDuration } from "./analyticsData";
import {
  applyFilters,
  caseHrefFor,
  EMPTY_FILTERS,
  isFilterActive,
  sortInspections,
} from "./inspections";

/**
 * The inspections behind the charts, filterable.
 *
 * The charts above say what is happening; this says to whom. So the two are
 * wired together — clicking a warning bar or a day narrows this list rather
 * than opening a separate view.
 *
 * Colour is not carrying any of the filtering: every chip is an icon plus a
 * word, every filter is a labelled select with its row count, and the one
 * status colour stays reserved for failure. The table is its own legend.
 */

// Words, not marks: 4.5:1 is the bar for text and #d03b3b does not clear it on
// this surface. These are the app's own chip foregrounds, so a failed
// inspection reads the same here as it does on the case list.
const ALERT_INK = "#c22f2f";
const WARN_INK = "#8a5c07";

const cell = "px-3 py-[9px] align-middle";
const headCell =
  "sticky top-0 z-10 bg-cl-head px-3 py-[8px] text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label";

/** No Date parsing: "2026-08-31 09:14:48" is not a format every browser reads
 *  the same way, and this table sorts on the raw string anyway. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatStamp = (value) => {
  if (typeof value !== "string" || value.length < 10) return "—";
  // Some backends send "YYYY-MM-DDTHH:MM:SS" instead of the space-separated
  // shape this was written for — normalize before splitting, same fix as
  // peopleList.js's formatModified, or the day and time both silently drop.
  const [date, time = ""] = value.replace("T", " ").split(" ");
  const [year, month, day] = date.split(/[-/]/);
  const name = MONTHS[Number(month) - 1];
  if (!name) return value;
  return `${Number(day)} ${name} ${year}${time ? `, ${time.slice(0, 5)}` : ""}`;
};

function Choice({ label, value, onChange, options, hint, width = 118 }) {
  return (
    <label className="flex flex-col gap-[4px]">
      <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
        {label}
        {hint ? <span className="ml-1 font-normal normal-case text-cl-faint">{hint}</span> : null}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{ width }}
        className="rounded-tile border border-cl-control bg-cl-surface px-[9px] py-[6px] text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortHeader({ label, sortKey, sort, onSort, align = "left", title }) {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      className={headCell}
      style={{ textAlign: align }}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      title={title}
    >
      <button
        type="button"
        onClick={() =>
          onSort({
            key: sortKey,
            dir: active && sort.dir === "desc" ? "asc" : "desc",
          })
        }
        className="inline-flex items-center gap-[3px] uppercase tracking-[0.06em] transition-colors hover:text-cl-text"
      >
        {label}
        <span className={active ? "text-cl-text" : "text-transparent"}>
          {active && sort.dir === "asc" ? "▲" : "▼"}
        </span>
      </button>
    </th>
  );
}

/**
 * Result and review state are different facts, so they get different pills.
 *
 * Both wear the same chips the case list and the case detail page already use,
 * so a status means the same thing and looks the same wherever it appears in
 * the portal — and each carries its word, so the colour is never the message.
 */
function ResultChip({ result }) {
  if (!result) return <span className="text-cl-faint">—</span>;
  const failed = result === "failed";
  const chip = getClearlineStatusChip(failed ? "Rejected" : "Completed");
  return (
    <span
      className="inline-flex items-center gap-[4px] rounded-full px-[8px] py-[3px] text-[11.5px] font-semibold"
      style={{ background: chip.bg, color: chip.fg }}
    >
      {failed ? (
        <XCircle className="h-[11px] w-[11px] flex-none" />
      ) : (
        <CheckCircle2 className="h-[11px] w-[11px] flex-none" />
      )}
      {result[0].toUpperCase() + result.slice(1)}
    </span>
  );
}

/**
 * The chip map keys on the case list's wording, and getStatusInfo returns a
 * slightly different one for the same state — "Review Completed" where the map
 * says "Completed", "Failed" where it says "Rejected" — so every one of those
 * fell through to the grey fallback.
 *
 * Mapped here rather than in the shared helper on purpose: adding these keys to
 * CLEARLINE_STATUS_CHIPS would also recolour the redesigned case list and case
 * detail page, which nobody asked for. (Those pages have the same grey-chip
 * problem today, and it is worth fixing there separately.)
 */
const CHIP_ALIASES = {
  "Review Completed": "Completed",
  Failed: "Rejected",
  "In-Process": "Processing",
  "Awaiting Retry": "Awaiting Retry",
};

function StatePill({ label }) {
  if (!label) return <span className="text-cl-faint">—</span>;
  const chip = getClearlineStatusChip(CHIP_ALIASES[label] || label);
  return (
    <span
      className="inline-flex items-center rounded-full px-[8px] py-[3px] text-[11.5px] font-medium"
      style={{ background: chip.bg, color: chip.fg }}
    >
      {label}
    </span>
  );
}

const PAGE = 50;

export default function InspectionsTable({
  inspections,
  facets,
  filters,
  onChange,
  identityLabel = "Plate",
  showPanels = false,
  reviewLabelFor = (state) => state,
  coverage,
  caseBasePath = "/cases",
}) {
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [visible, setVisible] = useState(PAGE);
  const [expanded, setExpanded] = useState(null);

  const rows = useMemo(
    () => sortInspections(applyFilters(inspections, filters), sort),
    [inspections, filters, sort],
  );

  const set = (patch) => {
    onChange({ ...filters, ...patch });
    setVisible(PAGE);
  };

  const withCount = (entries, allLabel) => [
    { value: "all", label: allLabel },
    ...entries.map((entry) => ({
      value: entry.key,
      label: `${entry.label} (${entry.count})`,
    })),
  ];

  // Only the filters this line can actually answer. Property inspections report
  // no panels, so a panel filter there would be a control that does nothing.
  const chips = [
    filters.day && { key: "day", text: `Day ${filters.day}`, clear: { day: null } },
    filters.country !== "all" && {
      key: "country",
      text: facets.countries.find((entry) => entry.key === filters.country)?.label || filters.country,
      clear: { country: "all" },
    },
    filters.result !== "all" && {
      key: "result",
      text: filters.result === "failed" ? "Failed" : "Passed",
      clear: { result: "all" },
    },
    filters.reviewState !== "all" && {
      key: "review",
      text: reviewLabelFor(filters.reviewState),
      clear: { reviewState: "all" },
    },
    filters.warning !== "all" && {
      key: "warning",
      text:
        filters.warning === "any"
          ? "Any warning"
          : filters.warning === "none"
            ? "No warnings"
            : facets.warnings.find((entry) => entry.code === filters.warning)?.label ||
              filters.warning,
      clear: { warning: "all" },
    },
    filters.rating !== "all" && {
      key: "rating",
      text:
        filters.rating === "rated"
          ? "Rated"
          : filters.rating === "unrated"
            ? "Not rated"
            : `${filters.rating} out of 5`,
      clear: { rating: "all" },
    },
    filters.comment !== "all" && {
      key: "comment",
      text: filters.comment === "with" ? "With comment" : "Without comment",
      clear: { comment: "all" },
    },
    filters.part !== "all" && {
      key: "part",
      text:
        facets.parts.find((entry) => entry.key === filters.part)?.label ||
        filters.part,
      clear: { part: "all" },
    },
    filters.query && { key: "query", text: `"${filters.query}"`, clear: { query: "" } },
  ].filter(Boolean);

  return (
    <div className="rounded-card border border-cl-card bg-cl-surface">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-cl-head px-4 py-[11px]">
        <span className="text-[13.5px] font-semibold text-cl-text">Inspections</span>
        <span className="text-[11px] text-cl-faint">
          {rows.length === facets.total
            ? `${facets.total.toLocaleString()} read`
            : `${rows.length.toLocaleString()} of ${facets.total.toLocaleString()} read`}
          {coverage && !coverage.complete
            ? ` · the period holds ${coverage.total.toLocaleString()}`
            : ""}
        </span>
      </div>

      {/* Filters in one row above the table. Each carries its own row count, so
          a filter that would return nothing says so before it is used. */}
      <div className="flex flex-wrap items-end gap-3 border-b border-cl-row px-4 py-[12px]">
        <label className="flex flex-col gap-[4px]">
          <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
            Search
          </span>
          <span className="relative">
            <Search className="pointer-events-none absolute left-[8px] top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-cl-faint" />
            <input
              type="text"
              value={filters.query}
              onChange={(event) => set({ query: event.target.value })}
              placeholder={`ID, ${identityLabel.toLowerCase()}, comment`}
              className="w-[196px] rounded-tile border border-cl-control bg-cl-surface pl-[26px] pr-[9px] py-[6px] text-[12.5px] text-cl-text outline-none placeholder:text-cl-faint focus:border-cl-accent"
            />
          </span>
        </label>

        <Choice
          label="Country"
          hint="from ID"
          value={filters.country}
          onChange={(value) => set({ country: value })}
          options={withCount(facets.countries, "All countries")}
          width={138}
        />
        <Choice
          label="Result"
          value={filters.result}
          onChange={(value) => set({ result: value })}
          options={withCount(
            facets.results.map((entry) => ({
              ...entry,
              label: entry.key[0].toUpperCase() + entry.key.slice(1),
            })),
            "Any result",
          )}
        />
        <Choice
          label="Review state"
          value={filters.reviewState}
          onChange={(value) => set({ reviewState: value })}
          options={withCount(facets.reviewStates, "Any state")}
          width={148}
        />
        <Choice
          label="Warning"
          value={filters.warning}
          onChange={(value) => set({ warning: value })}
          options={[
            { value: "all", label: "Any" },
            { value: "any", label: `With a warning (${facets.withWarning})` },
            { value: "none", label: `Without (${facets.total - facets.withWarning})` },
            ...facets.warnings.map((entry) => ({
              value: entry.code,
              label: `${entry.label} (${entry.count})`,
            })),
          ]}
          width={228}
        />
        <Choice
          label="Rating"
          hint={`${facets.withRating} rated`}
          value={filters.rating}
          onChange={(value) => set({ rating: value })}
          options={[
            { value: "all", label: "Any" },
            { value: "rated", label: `Rated (${facets.withRating})` },
            { value: "unrated", label: `Not rated (${facets.total - facets.withRating})` },
            ...facets.ratings.map((entry) => ({
              value: entry.key,
              label: `${entry.key} out of 5 (${entry.count})`,
            })),
          ]}
        />
        <Choice
          label="Comment"
          hint={`${facets.withComment} left one`}
          value={filters.comment}
          onChange={(value) => set({ comment: value })}
          options={[
            { value: "all", label: "Any" },
            { value: "with", label: `With a comment (${facets.withComment})` },
            { value: "without", label: `Without (${facets.total - facets.withComment})` },
          ]}
        />
        {showPanels && facets.parts.length > 0 && (
          <Choice
            label="Damaged panel"
            value={filters.part}
            onChange={(value) => set({ part: value })}
            options={withCount(facets.parts, "Any panel")}
            width={142}
          />
        )}
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-cl-row px-4 py-[9px]">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => set(chip.clear)}
              className="inline-flex items-center gap-[5px] rounded-full border border-cl-control px-[9px] py-[3px] text-[11.5px] text-cl-secondary transition-colors hover:bg-cl-hover"
            >
              {chip.text}
              <X className="h-[11px] w-[11px]" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange({ ...EMPTY_FILTERS })}
            className="text-[11.5px] font-semibold text-cl-accent hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="px-4 py-[34px] text-center">
          <p className="text-[12.5px] text-cl-muted">
            {facets.total === 0
              ? "No inspections were returned for this period."
              : "No inspections match these filters."}
          </p>
          {isFilterActive(filters) && (
            <button
              type="button"
              onClick={() => onChange({ ...EMPTY_FILTERS })}
              className="mt-3 rounded-tile border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="cl-scroll max-h-[560px] overflow-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr>
                  <th scope="col" className={`${headCell} w-[26px]`} />
                  <SortHeader label="Inspection ID" sortKey="ref" sort={sort} onSort={setSort} />
                  <SortHeader label={identityLabel} sortKey="identity" sort={sort} onSort={setSort} />
                  <SortHeader label="Captured" sortKey="date" sort={sort} onSort={setSort} />
                  <SortHeader label="Result" sortKey="result" sort={sort} onSort={setSort} />
                  <SortHeader label="Review state" sortKey="reviewState" sort={sort} onSort={setSort} />
                  <SortHeader label="Country" sortKey="country" sort={sort} onSort={setSort} />
                  <SortHeader label="Photos" sortKey="photos" sort={sort} onSort={setSort} align="right" />
                  <SortHeader
                    label="Time"
                    sortKey="duration"
                    sort={sort}
                    onSort={setSort}
                    align="right"
                    title="Capture duration, recorded on only some inspections"
                  />
                  <SortHeader label="Warnings" sortKey="warnings" sort={sort} onSort={setSort} align="right" />
                  <SortHeader label="Rating" sortKey="rating" sort={sort} onSort={setSort} align="right" />
                  <th scope="col" className={`${headCell} w-[54px]`}>
                    <span className="inline-flex items-center gap-[3px]" title="Surveyor comment">
                      <MessageSquare className="h-[10px] w-[10px]" />
                      Note
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, visible).map((row) => {
                  const open = expanded === row.refNum;
                  return (
                    <React.Fragment key={row.refNum}>
                      <tr
                        className="cursor-pointer border-t border-cl-row transition-colors hover:bg-cl-hover"
                        onClick={() => setExpanded(open ? null : row.refNum)}
                        /* inset, not border-left: a border on a <tr> in a
                           collapsing table does not render. */
                        style={
                          row.result === "failed"
                            ? { boxShadow: `inset 3px 0 0 ${ALERT_INK}` }
                            : undefined
                        }
                      >
                        <td className={`${cell} text-cl-faint`}>
                          {open ? (
                            <ChevronDown className="h-[13px] w-[13px]" />
                          ) : (
                            <ChevronRight className="h-[13px] w-[13px]" />
                          )}
                        </td>
                        <td className={`${cell} font-medium text-cl-text`}>
                          <span className="inline-flex items-center gap-[6px]">
                            <span className="max-w-[210px] truncate" title={row.refNum}>
                              {row.refNum}
                            </span>
                            {row.isTest && (
                              <span className="rounded-[3px] border border-cl-control px-[4px] py-[1px] text-[9.5px] uppercase tracking-[0.04em] text-cl-faint">
                                test
                              </span>
                            )}
                          </span>
                        </td>
                        <td className={`${cell} text-cl-secondary`}>
                          {row.identity || <span className="text-cl-faint">—</span>}
                        </td>
                        <td className={`${cell} whitespace-nowrap text-cl-secondary`}>
                          {formatStamp(row.createdAt)}
                        </td>
                        <td className={cell}>
                          <ResultChip result={row.result} />
                        </td>
                        <td className={cell}>
                          <StatePill
                            label={row.reviewState ? reviewLabelFor(row.reviewState) : null}
                          />
                        </td>
                        <td className={`${cell} text-cl-secondary`}>{row.country.label}</td>
                        <td className={`${cell} text-right tabular-nums text-cl-secondary`}>
                          {row.photos || "—"}
                        </td>
                        <td className={`${cell} text-right tabular-nums text-cl-secondary`}>
                          {formatDuration(row.duration) || <span className="text-cl-faint">—</span>}
                        </td>
                        <td className={`${cell} text-right tabular-nums`}>
                          {row.warnings.length ? (
                            <span className="inline-flex items-center gap-[4px] text-cl-secondary">
                              <AlertTriangle
                                className="h-[11px] w-[11px]"
                                style={{ color: WARN_INK }}
                              />
                              {row.warnings.length}
                            </span>
                          ) : (
                            <span className="text-cl-faint">—</span>
                          )}
                        </td>
                        <td className={`${cell} text-right tabular-nums`}>
                          {row.rating ? (
                            <span className="inline-flex items-center gap-[3px] text-cl-secondary">
                              <Star className="h-[11px] w-[11px]" />
                              {row.rating.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-cl-faint">—</span>
                          )}
                        </td>
                        <td className={cell}>
                          {row.comment ? (
                            <MessageSquare className="h-[12px] w-[12px] text-cl-secondary" />
                          ) : (
                            <span className="text-cl-faint">—</span>
                          )}
                        </td>
                      </tr>

                      {open && (
                        <tr className="border-t border-cl-row bg-cl-bg">
                          <td />
                          <td colSpan={11} className="px-3 pb-[14px] pt-[10px]">
                            <div className="grid gap-4 md:grid-cols-3">
                              <Detail title={`Warnings (${row.warnings.length})`}>
                                {row.warnings.length ? (
                                  <ul className="space-y-[5px]">
                                    {row.warnings.map((warning) => (
                                      <li
                                        key={warning.code}
                                        className="leading-[1.45] text-cl-muted"
                                      >
                                        {warning.message}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-cl-faint">None raised.</p>
                                )}
                              </Detail>

                              <Detail title={`Damaged parts (${row.parts.length})`}>
                                {row.parts.length ? (
                                  <ul className="space-y-[5px]">
                                    {row.parts.map((part) => (
                                      <li key={part.name} className="leading-[1.45]">
                                        <span className="font-semibold text-cl-secondary">
                                          {part.label}
                                        </span>{" "}
                                        <span className="text-cl-muted">
                                          {part.typeLabels.join(", ") || "type not given"}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-cl-faint">
                                    {showPanels
                                      ? "No damage reported."
                                      : "Property inspections report no parts."}
                                  </p>
                                )}
                              </Detail>

                              <Detail title="Record">
                                <dl className="space-y-[5px]">
                                  <Field label="Captured">{row.createdAt || "—"}</Field>
                                  <Field label="Duration">
                                    {formatDuration(row.duration) || "not recorded"}
                                  </Field>
                                  <Field label="Failure reason">
                                    {row.failureReason || "—"}
                                  </Field>
                                  <Field label="Comment">
                                    {row.comment || "none left"}
                                  </Field>
                                  {row.rating && (
                                    <Field label="Surveyor rating">
                                      {row.rating.toFixed(1)} out of 5
                                    </Field>
                                  )}
                                </dl>
                                {/* The whole point of finding a row here is to
                                    go and look at it. A new tab because the
                                    filters that found it are worth keeping —
                                    and the icon is what says it opens one. */}
                                {caseHrefFor(row.refNum, caseBasePath) && (
                                  <a
                                    href={caseHrefFor(row.refNum, caseBasePath)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    className="mt-[11px] inline-flex items-center gap-[6px] rounded-tile border border-cl-control px-[11px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover hover:text-cl-text"
                                  >
                                    Open case details
                                    <ExternalLink className="h-[12px] w-[12px]" />
                                  </a>
                                )}
                              </Detail>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.length > visible && (
            <div className="border-t border-cl-row px-4 py-[10px] text-center">
              <button
                type="button"
                onClick={() => setVisible((current) => current + PAGE)}
                className="rounded-tile border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
              >
                Show {Math.min(PAGE, rows.length - visible)} more of{" "}
                {rows.length.toLocaleString()}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Detail({ title, children }) {
  return (
    <div>
      <div className="mb-[6px] text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
        {title}
      </div>
      <div className="text-[12px]">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex gap-2">
      <dt className="w-[104px] flex-none text-cl-faint">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-cl-muted">{children}</dd>
    </div>
  );
}
